import { Input } from '@/shared/components/ui/input';
import {
  navSearchClearButtonVariants,
  navSearchClearIconVariants,
  navSearchIconVariants,
  navSearchInputVariants,
} from '@/shared/config/sidebar';
import { cn } from '@/shared/lib/cn';
import { useLingui } from '@lingui/react/macro';
import { Search, X } from 'lucide-react';
import type { FocusEventHandler, KeyboardEventHandler } from 'react';

/** Search input for a sidebar list (compact) or mobile browse page (comfortable). */
export function NavSearch({
  value,
  onChange,
  placeholder,
  ariaLabel,
  size = 'compact',
  autoFocus,
  className,
  onFocus,
  onBlur,
  onKeyDown,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  size?: 'compact' | 'comfortable';
  autoFocus?: boolean;
  className?: string;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}) {
  const { t } = useLingui();
  const hasValue = value.length > 0;

  return (
    <div className={cn('relative', className)}>
      <Search aria-hidden className={navSearchIconVariants({ size })} />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={navSearchInputVariants({ size, hasValue })}
      />
      {hasValue && (
        <button
          type="button"
          aria-label={t`Clear search`}
          className={navSearchClearButtonVariants({ size })}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => onChange('')}
        >
          <X className={navSearchClearIconVariants({ size })} aria-hidden />
        </button>
      )}
    </div>
  );
}
